/* eslint-disable class-methods-use-this */
import DelfosNotificationsAPI from 'dashboard/api/delfosNotifications';
import {
  autoRegisterDelfosPushSubscription,
  getDelfosDeviceId,
  getDelfosNotificationPreferences,
  postDelfosMessageToServiceWorker,
} from 'dashboard/helper/pushHelper';

const HEARTBEAT_INTERVAL_MS = 10000;
const RECENT_ACTIVITY_MS = 60000;
const MANAGED_INBOX_IDS = [1, 2];
const SEEN_EVENT_LIMIT = 300;
const SOUND_COOLDOWN_MS = 8000;
const SOUND_STATE_KEY = 'delfos_notification_sound_state_v1';
const CUSTOM_NOTIFICATION_AUDIO = '/audio/delfos/notification.mp3';

class DelfosNotificationCoordinator {
  constructor() {
    this.initialized = false;
    this.app = null;
    this.lastActivityAt = Date.now();
    this.lastPresence = null;
    this.heartbeatTimer = null;
    this.routeTimer = null;
    this.seenEvents = new Set();
    this.audio = null;
    this.audioUnlocked = false;
    this.boundServiceWorkerMessage = event =>
      this.handleServiceWorkerMessage(event);
    this.boundActivity = () => {
      this.lastActivityAt = Date.now();
    };
    this.boundUnlockAudio = () => this.unlockAudio();
    this.boundVisibility = () => {
      this.syncServiceWorkerState();
      this.syncPresence();
    };
  }

  initialize(app) {
    if (this.initialized) return;
    this.initialized = true;
    this.app = app;
    this.audio = new Audio(CUSTOM_NOTIFICATION_AUDIO);
    this.audio.preload = 'auto';

    ['pointerdown', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach(
      eventName =>
        window.addEventListener(eventName, this.boundActivity, {
          passive: true,
        })
    );
    ['pointerdown', 'keydown', 'touchstart'].forEach(eventName =>
      window.addEventListener(eventName, this.boundUnlockAudio, {
        passive: true,
      })
    );
    window.addEventListener('focus', this.boundVisibility);
    window.addEventListener('blur', this.boundVisibility);
    document.addEventListener('visibilitychange', this.boundVisibility);
    navigator.serviceWorker?.addEventListener(
      'message',
      this.boundServiceWorkerMessage
    );

    this.heartbeatTimer = window.setInterval(
      () => this.syncPresence(),
      HEARTBEAT_INTERVAL_MS
    );
    this.routeTimer = window.setInterval(() => {
      this.syncServiceWorkerState();
      this.syncPresence();
    }, 1500);

    autoRegisterDelfosPushSubscription()
      .then(() => {
        postDelfosMessageToServiceWorker({ type: 'DELFOS_LOGIN' });
        this.syncServiceWorkerState();
        return this.refreshPending();
      })
      .catch(() => {});
    this.syncServiceWorkerState();
    this.syncPresence();
  }

  syncServiceWorkerState(requestId = null) {
    postDelfosMessageToServiceWorker({
      type: 'DELFOS_CLIENT_STATE',
      foreground: this.isForeground(),
      audio_ready: this.audioUnlocked,
      request_id: requestId,
    });
  }

  async handleServiceWorkerMessage(event) {
    const message = event.data || {};
    if (message.type !== 'DELFOS_PLAY_SOUND') {
      this.handleRealtime(message);
      return;
    }

    const handled = await this.handleAudioRequest(message.payload);
    event.ports?.[0]?.postMessage({ handled });
  }

  async handleAudioRequest(payload) {
    const deviceId = getDelfosDeviceId();
    if (
      !payload ||
      this.isExpired(payload) ||
      (payload.target_device_id && payload.target_device_id !== deviceId)
    ) {
      return false;
    }

    const result = await this.playSound(payload);
    return result !== 'blocked';
  }

  isManagedMessage(message) {
    return MANAGED_INBOX_IDS.includes(Number(message?.inbox_id));
  }

  handleRealtime(payload) {
    if (!payload || Number(payload.protocol) !== 1) return;
    if (payload.type === 'DELFOS_FOCUS_PROBE') {
      this.syncServiceWorkerState(payload.request_id);
      return;
    }

    const deviceId = getDelfosDeviceId();
    if (payload.target_device_id && payload.target_device_id !== deviceId) {
      return;
    }

    if (payload.kind === 'retract') {
      postDelfosMessageToServiceWorker({
        type: 'DELFOS_RETRACT',
        tag: payload.tag,
        accountId: payload.account_id,
      });
      this.emitUIUpdate({ kind: 'retract', payload });
      return;
    }

    if (this.isExpired(payload) || this.hasSeen(payload.event_id)) return;
    this.remember(payload.event_id);
    this.playSound(payload).catch(() => {});

    if (this.isForeground()) {
      this.emitUIUpdate({ kind: 'notification', payload });
    }
  }

  async refreshPending() {
    const accountId = this.currentAccountId();
    if (!accountId || !this.isEnabled()) return;

    try {
      const { data } = await DelfosNotificationsAPI.getPending(accountId);
      this.emitUIUpdate({ kind: 'pending', payload: data });
    } catch (error) {
      // The notification center remains fail-closed when the session is gone.
    }
  }

  async syncPresence() {
    const accountId = this.currentAccountId();
    const displayId = this.activeConversationDisplayId();
    const shouldBeActive =
      this.isEnabled() &&
      accountId &&
      displayId &&
      this.isForeground() &&
      Date.now() - this.lastActivityAt <= RECENT_ACTIVITY_MS;

    const nextPresence = shouldBeActive
      ? `${accountId}:${displayId}:${getDelfosDeviceId()}`
      : null;
    if (nextPresence === this.lastPresence && shouldBeActive) {
      await this.sendHeartbeat(accountId, displayId);
      return;
    }

    if (this.lastPresence) {
      const [previousAccountId, previousDisplayId] =
        this.lastPresence.split(':');
      this.releasePresence(previousAccountId, previousDisplayId);
    }

    this.lastPresence = nextPresence;
    if (shouldBeActive) {
      await this.sendHeartbeat(accountId, displayId);
    }
  }

  async sendHeartbeat(accountId, conversationDisplayId) {
    try {
      await DelfosNotificationsAPI.heartbeat(accountId, {
        conversation_display_id: conversationDisplayId,
        device_id: getDelfosDeviceId(),
      });
    } catch (error) {
      this.lastPresence = null;
    }
  }

  releasePresence(accountId, conversationDisplayId) {
    DelfosNotificationsAPI.releasePresence(accountId, {
      conversation_display_id: conversationDisplayId,
      device_id: getDelfosDeviceId(),
    }).catch(() => {});
  }

  activeConversationDisplayId() {
    const match = window.location.pathname.match(
      /\/app\/accounts\/\d+\/conversations\/(\d+)/
    );
    return match?.[1] || null;
  }

  currentAccountId() {
    return this.app?.$store?.getters?.getCurrentAccountId || null;
  }

  isForeground() {
    return document.visibilityState === 'visible' && document.hasFocus();
  }

  isEnabled() {
    const preferences = getDelfosNotificationPreferences();
    const mutedUntil = preferences.mutedUntil
      ? new Date(preferences.mutedUntil).getTime()
      : 0;
    return preferences.enabled && (!mutedUntil || mutedUntil <= Date.now());
  }

  async unlockAudio() {
    if (!this.audio || this.audioUnlocked) return;

    const wasMuted = this.audio.muted;
    try {
      this.audio.muted = true;
      this.audio.currentTime = 0;
      await this.audio.play();
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audioUnlocked = true;
      this.syncServiceWorkerState();
    } catch (error) {
      // A later explicit interaction can retry the unlock.
    } finally {
      this.audio.muted = wasMuted;
    }
  }

  async playSound(payload) {
    const preferences = getDelfosNotificationPreferences();
    if (!preferences.soundEnabled || payload.silent) return 'disabled';
    if (this.hasRecentSound(payload.tag)) return 'suppressed';

    const play = async lock => {
      if (!lock) return 'suppressed';
      try {
        this.audio.currentTime = 0;
        await this.audio.play();
        this.audioUnlocked = true;
        this.rememberSound(payload.tag);
        this.syncServiceWorkerState();
        return 'played';
      } catch (error) {
        window.dispatchEvent(
          new CustomEvent('delfos:notification-audio-blocked')
        );
        return 'blocked';
      }
    };

    if (navigator.locks?.request) {
      return navigator.locks.request(
        `delfos-audio-${payload.tag || payload.event_id}`,
        { ifAvailable: true },
        play
      );
    }

    return play(true);
  }

  hasRecentSound(tag) {
    if (!tag) return false;
    const state = this.readSoundState();
    return Date.now() - Number(state[tag] || 0) < SOUND_COOLDOWN_MS;
  }

  rememberSound(tag) {
    if (!tag) return;
    const now = Date.now();
    const state = Object.fromEntries(
      Object.entries(this.readSoundState()).filter(
        ([, timestamp]) => now - Number(timestamp) < SOUND_COOLDOWN_MS * 4
      )
    );
    state[tag] = now;
    localStorage.setItem(SOUND_STATE_KEY, JSON.stringify(state));
  }

  readSoundState() {
    try {
      return JSON.parse(localStorage.getItem(SOUND_STATE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }

  isExpired(payload) {
    return (
      payload.expires_at && new Date(payload.expires_at).getTime() < Date.now()
    );
  }

  hasSeen(eventId) {
    return eventId && this.seenEvents.has(eventId);
  }

  remember(eventId) {
    if (!eventId) return;
    this.seenEvents.add(eventId);
    if (this.seenEvents.size > SEEN_EVENT_LIMIT) {
      this.seenEvents.delete(this.seenEvents.values().next().value);
    }
  }

  emitUIUpdate(detail) {
    window.dispatchEvent(
      new CustomEvent('delfos:notifications-updated', { detail })
    );
  }
}

export default new DelfosNotificationCoordinator();
